import { Tooltip } from '@mui/material'
import { MenuButton, MenuButtonItem } from '@repo/ui'
import { APP_FLOW_PAGES, DEVICE_OPTIONS } from './previewConfig'

/**
 * The certificate preview's own controls: which FLOW, which DEVICE, which PRODUCT.
 *
 * ## Why this is a component (VRG-827)
 *
 * It is rendered TWICE. `previewControlsPlacement()` (VRG-470) is an A/B flag whose
 * variants put these over the tab row or in the preview pane's floating band, and
 * StudioPage carried a full copy of the markup for each — same state, same handlers,
 * different parent. Adding the product selector to both copies is the second
 * hand-roll, which working-conventions §36 says is where a shared component starts.
 *
 * It also already HAD drifted, which is the usual tell: the in-pane copy carries no
 * widths because `MenuButton` types `sx` as `Record<string, string>` and its siblings
 * pass numbers. One source means the next control cannot be added to one placement and
 * forgotten in the other.
 *
 * ## `sized`
 *
 * The over-the-tab-row placement has room for set widths; the in-pane band sits beside
 * the zoom stepper and sizes to content. That is the ONLY difference between the two
 * call sites, so it is the only prop.
 *
 * ## The labels (VRG-827)
 *
 * All three triggers used to render a bare VALUE — `Certificate`, `Mobile`, and a product
 * name — so none of them said what axis it controlled, and `Certificate` was the worst of
 * the three because it is also the name of a Studio TAB and of one of the device-adjacent
 * nouns on the page. Each now carries its axis as a bold prefix inside the chip
 * (`MenuButton`'s `label`).
 *
 * ⚠️ The first one is **Screen**, and it is NOT "View". It is also not the Certificate/Email
 * tab row, which is a different control (`STUDIO_TABS`, rendered as `Tabs`): this one is
 * `APP_FLOW_PAGES` — `Certificates`, `Certificate`, `Certificate (public view)`,
 * `Claim ownership`, `Purchase product` — i.e. WHICH SCREEN of the customer app the preview
 * renders, via the app's `?page=` token. "Screen" is the only one-word label that reads
 * correctly across all five: "Flow" is the code's internal vocabulary and is wrong for
 * `Certificates`, which is a list rather than a flow, and "Page" collides with the tab row's
 * own sense of a page.
 *
 * The flow and device `sx` objects are byte-identical to the markup this replaces — an
 * extraction that silently restyles what it moves is not reviewable as an extraction.
 * They type-check now because `MenuButton.sx` was widened from `Record<string, string>`
 * to `Record<string, unknown>` in the same change: the narrow form rejected the numeric
 * dimensions and nested selectors every caller actually writes, which is what produced
 * the five baseline errors AND what made the in-pane placement drop its widths. Both
 * always worked at runtime.
 */
export interface PreviewControlsProps {
  previewPage: MenuButtonItem
  onPreviewPageChange: (item: MenuButtonItem) => void
  device: MenuButtonItem
  onDeviceChange: (item: MenuButtonItem) => void
  /**
   * The brand's products, as selector rows. EMPTY means the brand has none — the
   * control is then not rendered at all rather than shown empty or disabled.
   *
   * A disabled dropdown would be the §46 treatment for a PERMISSION block, where the
   * user needs to know the capability exists and who can grant it. This is record
   * state, resolvable by the user one surface away (add a product), so §46 says hide:
   * the preview still works on the mock, and an empty picker explains nothing.
   */
  productOptions: MenuButtonItem[]
  product: MenuButtonItem | null
  onProductChange: (item: MenuButtonItem) => void
  /** Set widths (over the tab row); omit to size to content (in the preview band). */
  sized?: boolean
}

export const PreviewControls = ({
  previewPage,
  onPreviewPageChange,
  device,
  onDeviceChange,
  productOptions,
  product,
  onProductChange,
  sized = false,
}: PreviewControlsProps) => (
  <>
    <MenuButton
      key="studio-flow"
      label="Screen"
      {...(sized
        ? { sx: { minWidth: 180, '& .MuiMenu-paper, & .MuiMenu-list': { minWidth: 220 } } }
        : {})}
      selected={previewPage}
      items={APP_FLOW_PAGES}
      onSelect={onPreviewPageChange}
      variant="secondary"
    />
    <MenuButton
      key="studio-device"
      label="Device"
      {...(sized
        ? { sx: { minWidth: 130, '& .MuiMenu-paper, & .MuiMenu-list': { minWidth: 170 } } }
        : {})}
      selected={device}
      items={DEVICE_OPTIONS}
      onSelect={onDeviceChange}
      variant="secondary"
    />
    {/* Hidden, not disabled, when the brand has no products — see `productOptions`.
        `search` because a catalogue is unbounded: the flow and device menus have four
        and three fixed rows, this one can have hundreds. */}
    {productOptions.length > 0 && product && (
      /*
        The ONLY one of the three that needs a tooltip, and the only one capped.
        A product name has no length limit, so this chip's width was effectively
        unbounded — measured at ~300px against ~130px for its neighbours purely because
        the brand's product happened to be called "Cartier Love Bracelet Yellow Gold".

        ⚠️ The cap goes in `buttonProps.sx`, NOT in `sx`. `sx` is the MENU's channel (see
        the prop's own doc), which is exactly how this was broken before: the `maxWidth: 240`
        that used to sit in `sx` here bounded the MENU and left the chip free to grow to
        365px, while the 240px menu it did bound was what forced a long product name onto
        three lines — i.e. one mis-aimed prop produced both of the bugs on this control.
        `sx` is left as it was so the menu keeps the width it had.

        The cap applies in BOTH placements, unlike the other two chips' `sized` widths. Those
        set a MINIMUM, which only matters where there is room to spend; this sets a MAXIMUM,
        and the in-pane band — floating over a preview beside the zoom stepper — is exactly
        where an unbounded chip does the most damage.

        The tooltip is unconditional rather than measured-on-overflow: whether the name is
        actually clipped depends on the rendered text width, and a tooltip that sometimes
        repeats a fully visible name is a much smaller cost than one that is missing on the
        name that needed it. The full name is also still in the menu.
      */
      <Tooltip title={product.label}>
        <span>
          <MenuButton
            key="studio-product"
            label="Product"
            buttonProps={{ sx: { maxWidth: 240 } }}
            {...(sized ? { sx: { minWidth: 200, maxWidth: 240 } } : {})}
            selected={product}
            items={productOptions}
            onSelect={onProductChange}
            variant="secondary"
            search
          />
        </span>
      </Tooltip>
    )}
  </>
)
